import { Link, useParams } from "react-router-dom";
import { useSharedPortfolio } from "@/hooks/useData";
import { Logo, ThemeToggle } from "@/components/AppShell";
import { Sparkline } from "@/components/widgets/StatWidgets";
import { EquityCurveChart } from "@/components/widgets/ChartWidgets";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtPct, pnlClass } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Eye, AlertCircle, Landmark, Briefcase, LineChart, ShieldCheck, FlaskConical } from "lucide-react";

function Stat({ label, value, cls, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("font-display text-xl font-semibold tabular-nums", cls)} data-testid={testId}>{value}</div>
    </div>
  );
}

function AccountCard({ a }) {
  const active = a.status === "active" || a.is_demo;
  return (
    <div className="rounded-2xl border bg-card p-4" data-testid={`shared-portfolio-account-${a.account_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
            <Landmark className="h-4 w-4 text-primary" />
          </span>
          <div>
            <div className="text-sm font-semibold">{a.name}</div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span>{a.broker || a.server || "\u2014"} · {(a.platform || "").toUpperCase()}</span>
              {a.account_type && (
                <span className={cn("inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-semibold uppercase",
                  a.account_type === "real" ? "bg-[hsl(var(--profit))]/10 text-[hsl(var(--profit))]" : "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]")}>
                  {a.account_type === "real" ? <ShieldCheck className="h-2.5 w-2.5" /> : <FlaskConical className="h-2.5 w-2.5" />}
                  {a.account_type}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {active ? (
        <>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <div className="font-display text-xl font-semibold tabular-nums">{fmtMoney(a.balance, a.currency)}</div>
              <div className={cn("text-xs font-medium tabular-nums", pnlClass(a.total_gain_pct))}>{fmtPct(a.total_gain_pct, true)} all-time</div>
            </div>
            <Sparkline points={a.sparkline} width={110} height={34}
              className={a.total_gain_pct >= 0 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"} />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 border-t pt-3 text-center">
            <div><div className="text-xs font-semibold tabular-nums">{a.win_rate}%</div><div className="text-[10px] text-muted-foreground">Win rate</div></div>
            <div><div className="text-xs font-semibold tabular-nums text-[hsl(var(--loss))]">-{fmtPct(a.max_drawdown)}</div><div className="text-[10px] text-muted-foreground">Max DD</div></div>
            <div><div className="text-xs font-semibold tabular-nums">{a.profit_factor}</div><div className="text-[10px] text-muted-foreground">P. factor</div></div>
          </div>
          <Button asChild variant="outline" size="sm" className="mt-3 h-7 w-full gap-1.5 text-xs">
            <Link to={`/shared/account/${a.share_token}`} data-testid={`shared-portfolio-view-${a.account_id}`}>
              <LineChart className="h-3.5 w-3.5" /> View analytics
            </Link>
          </Button>
        </>
      ) : (
        <p className="mt-4 rounded-xl bg-surface-2 p-3 text-xs text-muted-foreground">This account is still syncing with its broker.</p>
      )}
    </div>
  );
}

export default function SharedPortfolioPage() {
  const { token } = useParams();
  const { data, isLoading, error } = useSharedPortfolio(token);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-4 text-center" data-testid="shared-portfolio-error">
        <AlertCircle className="h-8 w-8 text-[hsl(var(--warning))]" />
        <h1 className="font-display text-xl font-semibold">
          {error?.response?.status === 404 ? "This share link doesn't exist or was revoked." : "Couldn't load this portfolio."}
        </h1>
        <p className="text-sm text-muted-foreground">Ask the owner for a fresh link.</p>
        <Button asChild variant="secondary"><Link to="/">Go to My Trading Room</Link></Button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-screen space-y-4 bg-background p-6">
        <Skeleton className="h-12 w-72" />
        <Skeleton className="h-64" />
        <div className="grid grid-cols-3 gap-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48" />)}</div>
      </div>
    );
  }

  const pf = data.portfolio;
  const agg = data.aggregate || {};

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="shared-portfolio-page">
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur">
        <div className="flex h-14 items-center gap-3 px-4 lg:px-6">
          <Link to="/"><Logo /></Link>
          <div className="ml-2 hidden min-w-0 sm:block">
            <div className="truncate font-display text-sm font-semibold">{pf.name}</div>
            <div className="text-[11px] text-muted-foreground">shared by {data.owner_name}</div>
          </div>
          <Badge variant="secondary" className="ml-2 gap-1 text-[10px]" data-testid="read-only-badge"><Eye className="h-3 w-3" /> Read-only</Badge>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <Button asChild size="sm" data-testid="shared-portfolio-cta-button"><Link to="/login">Build your own</Link></Button>
          </div>
        </div>
      </header>

      <main className="flex-1 bg-surface-2 px-3 py-4 sm:px-4 lg:px-6">
        <div className="mx-auto w-full max-w-6xl space-y-4">
          <div className="rounded-2xl border bg-card p-4 sm:p-5" data-testid="shared-portfolio-header">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10"><Briefcase className="h-5 w-5 text-primary" /></span>
              <div className="min-w-0">
                <h1 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">{pf.name}</h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  {pf.description || `${agg.account_count || 0} account${agg.account_count === 1 ? "" : "s"} · live read-only view`}
                </p>
              </div>
            </div>
          </div>

          {agg.account_count > 0 && (
            <div className="rounded-2xl border bg-card p-4 sm:p-5" data-testid="shared-portfolio-aggregate">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
                  <Stat label="Total balance" value={fmtMoney(agg.balance, agg.currency)} testId="shared-portfolio-balance" />
                  <Stat label="Equity" value={fmtMoney(agg.equity, agg.currency)} />
                  <Stat label="Total P/L" value={fmtMoney(agg.total_pl, agg.currency)} cls={pnlClass(agg.total_pl)} testId="shared-portfolio-pl" />
                  <Stat label="All-time gain" value={fmtPct(agg.total_gain_pct, true)} cls={pnlClass(agg.total_gain_pct)} />
                </div>
                <div className="flex gap-6 text-center">
                  <div><div className="text-xs font-semibold tabular-nums">{agg.win_rate}%</div><div className="text-[10px] text-muted-foreground">Win rate</div></div>
                  <div><div className="text-xs font-semibold tabular-nums">{agg.profit_factor}</div><div className="text-[10px] text-muted-foreground">P. factor</div></div>
                  <div><div className="text-xs font-semibold tabular-nums text-[hsl(var(--loss))]">-{fmtPct(agg.max_drawdown)}</div><div className="text-[10px] text-muted-foreground">Max DD</div></div>
                  <div><div className="text-xs font-semibold tabular-nums">{agg.total_trades}</div><div className="text-[10px] text-muted-foreground">Trades</div></div>
                </div>
              </div>
              <div className="mt-4 border-t pt-3">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Combined equity curve · {agg.account_count} account{agg.account_count === 1 ? "" : "s"}</div>
                <EquityCurveChart data={agg} height={180} currency={agg.currency} />
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="shared-portfolio-accounts">
            {(data.accounts || []).map((a) => <AccountCard key={a.account_id} a={a} />)}
          </div>
          {(data.accounts || []).length === 0 && (
            <div className="rounded-2xl border border-dashed bg-card p-10 text-center text-sm text-muted-foreground">No accounts in this portfolio yet.</div>
          )}
        </div>
      </main>

      <footer className="border-t bg-card px-4 py-3 text-center text-xs text-muted-foreground">
        Powered by <span className="font-semibold text-foreground">My Trading Room</span>. Build your own trading dashboard in minutes.
      </footer>
    </div>
  );
}
